import { get, flatten } from 'lodash';

export const calculateTotalWithoutMultiplier = priceSelections => {
  return Object.values(priceSelections).reduce(
    (accumulator, currentValue) => {
      accumulator[0] += currentValue.cost;
      accumulator[1] += currentValue.price;
      return accumulator;
    },
    [0, 0]
  );
};

export const calculateTotalWithMultiplier = (priceSelections, inputValues) => {
  const inputValuesArray = Object.values(inputValues || {});
  let multiplier = 0;
  if (
    inputValuesArray.length &&
    !inputValuesArray.some(inpVal => +inpVal === 0)
  ) {
    multiplier = inputValuesArray.reduce(
      (accumulator, currentValue) => accumulator * Number(currentValue),
      1
    );
  }

  return Object.values(priceSelections).reduce(
    (accumulator, currentValue) => {
      accumulator[0] += currentValue.cost * multiplier;
      accumulator[1] += currentValue.price * multiplier;
      return accumulator;
    },
    [0, 0]
  );
};

export const calculateTotalWithCustomSections = (_1, _2, selections) => {
  return selections.reduce(
    (accumulator, currentValue) => {
      accumulator[0] += +currentValue.serviceCost;
      accumulator[1] += +currentValue.servicePrice;
      return accumulator;
    },
    [0, 0]
  );
};

export const calculateTotalForFileSharingAndCollaboration = (
  priceSelections,
  inputValues,
  selections,
  extraData
) => {
  const inputValuesArray = Object.values(inputValues || {});
  let multiplier = get(
    extraData,
    'categoryNumberSelections.managedUserAndEndpoints.userSecurityAndMAnagement.numberOfUsers',
    0
  );
  if (
    inputValuesArray.length &&
    !inputValuesArray.some(inpVal => +inpVal === 0)
  ) {
    multiplier = inputValuesArray.reduce(
      (accumulator, currentValue) => accumulator * Number(currentValue),
      1
    );
  }

  return Object.values(priceSelections).reduce(
    (accumulator, currentValue) => {
      accumulator[0] += currentValue.cost * multiplier;
      accumulator[1] += currentValue.price * multiplier;
      return accumulator;
    },
    [0, 0]
  );
};

export const convertSelectionsGridDataToPayload = (data, schema) => {
  const {
    priceSelections,
    selections,
    sectionTotals,
    numberFields,
    categoryTotals,
    customSelections
  } = data;

  let result = {};
  if (schema.sections) {
    result = {
      categoryName: schema.categoryName,
      sections: schema.sections.map(section => {
        return {
          sectionName: section.title ? section.title : schema.categoryName,
          selections:
            section.items &&
            section.items.map(({ id, label }) => {
              return {
                label,
                value:
                  (selections[section.id] && selections[section.id][id]) || '',
                costPrice:
                  (priceSelections[section.id] &&
                    priceSelections[section.id][id]) ||
                  {}
              };
            }),
          sectionTotal:
            (sectionTotals[section.id] && {
              cost: sectionTotals[section.id][0],
              price: sectionTotals[section.id][1]
            }) ||
            {},
          inputs: [
            ...(section.numberField
              ? [
                  {
                    label: section.numberField.label,
                    value:
                      (numberFields[section.id] &&
                        numberFields[section.id][section.numberField.id]) ||
                      ''
                  }
                ]
              : [])
          ],
          customInputs: [
            ...(section.customInputs && customSelections[schema.categoryId]
              ? customSelections[schema.categoryId]
              : [])
          ]
        };
      }),
      categoryTotal:
        (categoryTotals && {
          cost: categoryTotals[0],
          price: categoryTotals[1]
        }) ||
        {}
    };
  }
  return result;
};

export const convertSelectionsGridDataToSimplePayload = (data, schemaItem) => {
  const simpleQuantites = schemaItem.sections.map(section => {
    if (get(section, 'numberField.quoteLabel', '')) {
      return {
        name: section.numberField.quoteLabel,
        quantity: +get(
          data.numberFields,
          `${section.id}.${section.numberField.id}`,
          0
        ),
        total: data.sectionTotals[section.id]
          ? data.sectionTotals[section.id][1]
          : 0
      };
    }
    return null;
  });
  return simpleQuantites;
};

export const getManagedITServicesSummaryPayload = (
  itServicesData,
  schema,
  productTotal
) => {
  const isMFAEnabled = get(
    itServicesData,
    'categorySelections.managedUserAndEndpoints.userSecurityAndMAnagement.isMfaEnabled',
    ''
  );
  const summary = schema.map(schemaItem => {
    const sectionTotals =
      (itServicesData.sectionTotals &&
        itServicesData.sectionTotals[schemaItem.categoryId]) ||
      {};
    const categoryTotals = (itServicesData.categoryTotals &&
      itServicesData.categoryTotals[schemaItem.categoryId]) || [0, 0];
    const numberFields =
      (itServicesData.categoryNumberSelections &&
        itServicesData.categoryNumberSelections[schemaItem.categoryId]) ||
      {};
    return convertSelectionsGridDataToSimplePayload(
      { sectionTotals, categoryTotals, numberFields },
      schemaItem
    );
  });

  const oneTimeFees = itServicesData.fees
    ? [
        {
          name: 'Onboarding Fee',
          total:
            get(itServicesData, 'fees.onboardingFee', 0) +
            get(itServicesData, 'fees.additionalOneTimeFee', 0)
        }
      ]
    : [];

  const additionalServices = get(
    itServicesData,
    'categoryCustomSelections.additionalServices.additionalServices',
    []
  ).map(additionalService => ({
    name: `Additional Service - ${additionalService.serviceDescription}`,
    quantity: 1,
    total: +additionalService.servicePrice
  }));

  const chargesUpdated = [
    ...flatten(summary).filter(summaryItem => {
      return summaryItem && summaryItem.quantity;
    }),
    ...additionalServices
  ];
  return {
    charges: chargesUpdated,
    mfaComment:
      isMFAEnabled !== 'MFA confirmed enabled'
        ? 'Price is dependent on project being completed within 3 months.'
        : isMFAEnabled,
    additionalCharges: oneTimeFees,
    totalMonthlyCharges: productTotal[1],
    totalOneTimeCharges: oneTimeFees.length
      ? oneTimeFees.reduce((acc, val) => acc + val.total, 0)
      : 0
  };
};

export const getManagedITServicesErrors = (schema, selections) => {
  const categorySelections = get(selections, 'categorySelections', {});
  const categoryNumberSelections = get(
    selections,
    'categoryNumberSelections',
    {}
  );
  const errors = {};
  schema.forEach(schemaItem => {
    errors[schemaItem.categoryId] = {};
    schemaItem.sections.forEach(sectionItem => {
      if (sectionItem.customGlobalValidation) {
        const errorAccordingToCustomInput = sectionItem.customGlobalValidation(
          categorySelections,
          categoryNumberSelections
        );
        if (errorAccordingToCustomInput) {
          const message = errorAccordingToCustomInput;
          errors[schemaItem.categoryId][sectionItem.id] = message;
        }
      }
      if (sectionItem.numberField) {
        let areOptionsSelected = true;
        const valueNumberField = get(
          categoryNumberSelections,
          `${schemaItem.categoryId}.${sectionItem.id}.${sectionItem.numberField.id}`,
          0
        );
        if (+valueNumberField) {
          sectionItem.items.forEach(dropdownItem => {
            if (
              !get(
                categorySelections,
                `${schemaItem.categoryId}.${sectionItem.id}.${dropdownItem.id}`,
                false
              )
            ) {
              areOptionsSelected = false;
            }
          });
          if (!areOptionsSelected) {
            const message = `Complete section must be filled if ${sectionItem.numberField.label.replace(
              '*',
              ''
            )} are entered`;
            errors[schemaItem.categoryId][sectionItem.id] = message;
          }
        }
        if (sectionItem.numberField.customValidation) {
          const errorAccordingToCustomInput = sectionItem.numberField.customValidation(
            valueNumberField || 0,
            categorySelections,
            categoryNumberSelections
          );
          if (errorAccordingToCustomInput) {
            const message = errorAccordingToCustomInput;
            errors[schemaItem.categoryId][sectionItem.id] = message;
          }
        }
      } else {
        let areOptionsSelected = true;
        if (sectionItem.items) {
          sectionItem.items.forEach(dropdownItem => {
            if (
              !get(
                categorySelections,
                `${schemaItem.categoryId}.${sectionItem.id}.${dropdownItem.id}`,
                false
              )
            ) {
              areOptionsSelected = false;
            }
          });
        }
        if (!areOptionsSelected) {
          const message = `Please fill out this section`;
          errors[schemaItem.categoryId][sectionItem.id] = message;
        }
      }
    });
  });
  return errors;
};
